// school-list.component.ts
import { Component, input, signal, ChangeDetectionStrategy } from '@angular/core';
import { School } from '@awo-care/shared-core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { SchoolDetailComponent } from './school-detail.component';
import { PopoverComponent } from '@awo-care/shared-ui';
import { SchoolCityGroup } from './schools-grouping.utils';

@Component({
  selector: 'app-school-list',
  imports: [
    MatProgressSpinnerModule,
    MatIconModule,
    SchoolDetailComponent,
    PopoverComponent,
  ],
  template: `
    @if (loading()) {
    <div class="spinner-container">
      <mat-spinner diameter="40"></mat-spinner>
    </div>
    } @else if (groups().length === 0) {
    <div class="empty-state">
      <mat-icon aria-hidden="true">search_off</mat-icon>
      <p i18n="@@schools.list.noSchoolFound">Keine Schulen gefunden</p>
    </div>
    } @else {
    <div class="school-groups">
      @for (group of groups(); track group.city) {
      <section class="city-group">
        <!--
          aria-level statt eines zweiten h1/h2/h3-Zweigs: die Optik haengt
          komplett an .awo-section-label, nur die Ebene muss der Ueberschrift
          der einbettenden Seite folgen (WCAG 1.3.1).
        -->
        <h3 class="awo-section-label" [attr.aria-level]="headingLevel()">
          {{ group.city }}
        </h3>
        <div class="tile-list">
          @for (school of group.schools; track school.id) {
          <div
            class="awo-card awo-list-tile"
            tabindex="0"
            (click)="openSchoolDetails(school)"
            (keyup.enter)="openSchoolDetails(school)"
            (keyup.space)="openSchoolDetails(school)"
          >
            <div class="awo-list-tile__avatar">
              <mat-icon aria-hidden="true">school</mat-icon>
            </div>
            <div class="awo-list-tile__body">
              <div class="awo-list-tile__title">{{ school.schoolName }}</div>
              <div class="awo-list-tile__meta">
                {{ school.address.zipCode }} {{ school.address.city }}
              </div>
            </div>
            <mat-icon aria-hidden="true" class="awo-list-tile__chevron">chevron_right</mat-icon>
          </div>
          }
        </div>
      </section>
      }
    </div>

    <app-popover [isOpen]="isPopoverOpen()" width="640px" (close)="closePopover()">
      @if (selectedSchool()) {
      <app-school-detail [school]="selectedSchool()!"></app-school-detail>
      }
    </app-popover>
    }
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
      }

      .empty-state {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .school-groups {
        display: flex;
        flex-direction: column;
        gap: 2rem;
        width: 100%;
        max-width: 1080px;
        margin: 0 auto;
      }

      .tile-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        gap: 0.75rem;
      }

      /*
       * Fallback styling for the .awo-* primitive classes used above.
       *
       * The primitives (.awo-card / .awo-section-label / .awo-list-tile) are
       * normally defined globally in customer-ui's _awo-primitives.scss and
       * driven by the --awo-* custom properties from its :root. This
       * component is also rendered standalone by the website app
       * (apps/website/src/app/home/home.component.ts <app-schools /> and its
       * own /schools route via PAGES_ROUTES) — an app that does NOT load
       * customer-ui's primitives stylesheet or define any --awo-* variables.
       *
       * These rules (scoped to this component only by Angular's emulated
       * view encapsulation — they cannot leak into other components) provide
       * the same visual recipe with graceful var() fallbacks, so the schools
       * list still looks like a proper card list on website instead of
       * rendering as unstyled divs. On customer-ui the --awo-* variables are
       * always defined, so the resolved values are identical to using the
       * global primitives directly; only the fallback literals are ever used
       * elsewhere.
       */
      .awo-card {
        background: var(--awo-card, #ffffff);
        border: 1px solid var(--awo-border, #eae6e2);
        border-radius: var(--awo-radius-lg, 16px);
        box-shadow:
          0 1px 2px rgba(28, 27, 26, 0.04),
          0 6px 20px rgba(28, 27, 26, 0.05);
        padding: 1.25rem 1.5rem;
      }

      .awo-section-label {
        text-transform: uppercase;
        letter-spacing: 0.08em;
        font-size: 0.75rem;
        font-weight: 700;
        color: var(--awo-text-subtle, #6f6a66);
        margin: 0 0 0.75rem;
      }

      .awo-list-tile {
        display: flex;
        align-items: center;
        gap: 1rem;

        &__avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--awo-red-tint, #fdeaec);
          color: var(--theme-primary, #e2001a);
          display: flex;
          align-items: center;
          justify-content: center;
          flex: 0 0 auto;
        }

        &__body {
          flex: 1 1 auto;
          min-width: 0;
        }

        &__title {
          font-weight: 700;
          color: var(--awo-text, #1c1b1a);
          overflow-wrap: anywhere;
          line-height: 1.3;
        }

        &__meta {
          color: var(--awo-text-subtle, #6f6a66);
          font-size: 0.85rem;
          margin-top: 0.15rem;
        }

        &__chevron {
          color: var(--awo-text-subtle, #6f6a66);
          flex: 0 0 auto;
        }
      }

      .awo-list-tile.awo-card {
        cursor: pointer;
        transition:
          transform 0.15s ease,
          box-shadow 0.15s ease;

        &:hover,
        &:focus-visible {
          transform: translateY(-1px);
        }
      }

    `,
  ],
})
export class SchoolListComponent {
  groups = input.required<SchoolCityGroup[]>();
  loading = input<boolean>();
  /** Ebene der Stadt-Ueberschriften — eine Stufe unter der Seiten-Ueberschrift. */
  headingLevel = input<2 | 3>(3);

  selectedSchool = signal<School | null>(null);

  isPopoverOpen = signal(false);

  openSchoolDetails(school: School) {
    this.selectedSchool.set(school);
    this.isPopoverOpen.set(true);
  }

  closePopover() {
    this.isPopoverOpen.set(false);
    this.selectedSchool.set(null);
  }
}
